import React, { useRef, useEffect } from 'react';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import { Line } from 'react-chartjs-2';
import styles from './TotalScansChart.module.css';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

export default function TotalScansChart({ data, title = "Total Scans Over Time" }) {
    const chartRef = useRef(null);

    // Calculate chart width based on data length for horizontal scrolling
    // For monthly view, use fewer pixels per data point since there are fewer points
    const pixelsPerPoint = data?.isMonthlyView ? 60 : 8;
    const minWidth = data?.isMonthlyView ? 600 : 800;
    const chartWidth = data?.needsHorizontalScroll ? 
        Math.max(minWidth, (data?.labels?.length || 0) * pixelsPerPoint) : '100%';

    const options = {
        responsive: !data?.needsHorizontalScroll, // Disable responsive for scrollable charts
        maintainAspectRatio: false,
        resizeDelay: 0,
        plugins: {
            legend: {
                display: false,
            },
            title: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#161d2d',
                titleColor: '#f6f6f6',
                bodyColor: '#f6f6f6',
                borderColor: '#161d2d',
                borderWidth: 1,
                cornerRadius: 8,
                displayColors: false,
                callbacks: {
                    title: function(context) {
                        return `Date: ${context[0].label}`;
                    },
                    label: function(context) {
                        return `Scans: ${context.parsed.y}`;
                    }
                }
            },
        },
        scales: {
            x: {
                grid: {
                    display: false,
                },
                border: {
                    display: false,
                },
                ticks: {
                    color: '#6b7280',
                    font: {
                        size: 11,
                    },
                    maxTicksLimit: data?.needsHorizontalScroll ? 15 : 7, // More labels for scrollable charts
                    maxRotation: 45, // Rotate labels if needed for better fit
                    minRotation: 0,
                },
            },
            y: {
                beginAtZero: true,
                grid: {
                    color: '#f3f4f6',
                    borderDash: [2, 2],
                },
                border: {
                    display: false,
                },
                ticks: {
                    color: '#6b7280',
                    font: {
                        size: 12,
                    },
                    maxTicksLimit: 8, // Limit number of y-axis labels for consistent spacing
                    callback: function(value) {
                        if (value >= 1000) {
                            return (value / 1000).toFixed(1) + 'k';
                        }
                        return value;
                    },
                },
            },
        },
        elements: {
            point: {
                radius: 4,
                hoverRadius: 6,
                backgroundColor: '#161d2d',
                borderColor: '#f6f6f6',
                borderWidth: 2,
            },
            line: {
                tension: 0.4,
                borderWidth: 3,
            },
        },
        interaction: {
            intersect: false,
            mode: 'index',
        },
    };

    const chartData = {
        labels: data?.labels || [],
        datasets: [
            {
                label: 'Total Scans',
                data: data?.data || [],
                borderColor: '#161d2d',
                backgroundColor: 'rgba(22, 29, 45, 0.1)',
                fill: true,
                tension: 0.4,
            },
        ],
    };

    // Calculate totals for display
    const totalScans = data?.data?.reduce((sum, value) => sum + value, 0) || 0;
    const averageScans = data?.data?.length ? Math.round(totalScans / data.data.length) : 0;
    const maxScans = data?.data?.length ? Math.max(...data.data) : 0;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.titleContainer}>
                    <h3 className={styles.title}>{title}</h3>
                    {data?.needsHorizontalScroll && (
                        <span className={styles.scrollHint}>
                            <i className="fa-solid fa-arrows-left-right"></i>
                            Scroll to view all {data?.isMonthlyView ? 'monthly' : 'daily'} data
                        </span>
                    )}
                </div>
                <div className={styles.stats}>
                    <div className={styles.stat}>
                        <span className={styles.statValue}>{totalScans}</span>
                        <span className={styles.statLabel}>Total</span>
                    </div>
                    <div className={styles.stat}>
                        <span className={styles.statValue}>{averageScans}</span>
                        <span className={styles.statLabel}>Avg/{data?.isMonthlyView ? 'Month' : 'Day'}</span>
                    </div>
                    <div className={styles.stat}>
                        <span className={styles.statValue}>{maxScans}</span>
                        <span className={styles.statLabel}>Peak</span>
                    </div>
                </div>
            </div>
            
            <div className={styles.chartContainer}>
                <div style={{ width: chartWidth, height: '300px' }}>
                    <Line 
                        key={`chart-${data?.aggregationType || 'daily'}-${data?.needsHorizontalScroll ? 'scrollable' : 'responsive'}-${data?.totalDays || 0}`}
                        ref={chartRef} 
                        options={options} 
                        data={chartData}
                        width={data?.needsHorizontalScroll ? chartWidth : null}
                        height={300}
                    />
                </div>
            </div>
            
            {(!data || !data.data || data.data.length === 0) && (
                <div className={styles.noData}>
                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M3 3v18h18"></path>
                        <path d="M18.7 8l-5.1 5.2-2.8-2.7L7 14.3"></path>
                    </svg>
                    <p>No scan data available for the selected period.</p>
                </div>
            )}
        </div>
    );
} 